/** Funnel stages — one rule for the requirement page's funnel counts and the lists its cards open.
 *
 *  The funnel is CUMULATIVE: a stage counts everyone who has reached it or gone past it. A card's
 *  "View" link carries `?stage=<key>`, and the Candidates / Interviews pages keep exactly the people
 *  that card counted, so the list length always matches the number the recruiter clicked. */
import type { CandidacyState } from "@talenthop/shared";

/** Rank each candidacy state so "reached this far" is a single comparison. */
export const STATE_RANK: Record<CandidacyState, number> = {
  sourced: 0,
  screened: 1,
  shortlisted: 2,
  invited: 3,
  self_scheduled: 4,
  reminded: 4,
  interviewing: 5,
  scored: 6,
  advanced: 7,
  proposed_cut: 6,
  cut_notified: 6,
  finalist: 7,
};

/** The stages a funnel card can link to (post mode shows Applied + Screened; direct shows Uploaded). */
export const FUNNEL_STAGES = {
  applied: { rank: 0, label: "Applied" },
  uploaded: { rank: 0, label: "Uploaded" },
  screened: { rank: 1, label: "Screened" },
  invited: { rank: 3, label: "Invited" },
  scheduled: { rank: 4, label: "Scheduled" },
  interviewed: { rank: 6, label: "Interviewed" },
  finalists: { rank: 7, label: "Finalists" },
} as const;

export type FunnelStageKey = keyof typeof FUNNEL_STAGES;

/** The stage named by a `?stage=` value, or null when absent/unknown (an unknown key filters nothing). */
export function funnelStageFromParam(v: string | null): { key: FunnelStageKey; rank: number; label: string } | null {
  if (!v || !(v in FUNNEL_STAGES)) return null;
  const key = v as FunnelStageKey;
  return { key, ...FUNNEL_STAGES[key] };
}

/** Has a candidacy in `state` reached a stage of `rank`? Unknown states rank 0, as in the funnel count. */
export const reachedStage = (state: string, rank: number): boolean =>
  (STATE_RANK[state as CandidacyState] ?? 0) >= rank;
